/**
 * Chat Service
 * Handles chat/message operations with agents
 *
 * Uses /generate endpoint - responses come via WebSocket
 */

import { TeamScopedService } from './base.service';
import type { ChatGenerateInput, ChatGenerateOutput } from '@zenmako/contracts/threads';

export interface SendMessageRequest {
  threadId: string;
  agentId: string;
  message: string;
  resourceId?: string;
}

class ChatService extends TeamScopedService {
  /**
   * Send a message to an agent
   *
   * Note: The response will come via WebSocket.
   * This method only triggers the generation.
   */
  async sendMessage(
    teamId: string,
    request: SendMessageRequest
  ): Promise<ChatGenerateOutput> {
    const body: ChatGenerateInput = {
      messages: [{ role: 'user', content: request.message }],
      agentId: request.agentId,
      threadId: request.threadId,
      resourceId: request.resourceId || `t#${teamId}`,
    };

    return this.postTeam<ChatGenerateOutput>(
      teamId,
      `/chat/${request.agentId}/generate`,
      body
    );
  }

  /**
   * Simplified send message method
   *
   * @deprecated Use sendMessage for better type safety
   */
  async sendMessageSimple(
    teamId: string,
    threadId: string,
    message: string,
    agentId: string
  ): Promise<ChatGenerateOutput> {
    return this.sendMessage(teamId, {
      threadId,
      agentId,
      message,
      resourceId: `t#${teamId}`,
    });
  }

  /**
   * Get chat availability status (future)
   */
  async getChatStatus(teamId: string, agentId: string): Promise<any> {
    return this.getTeam(teamId, `/chat/${agentId}/status`);
  }
}

export const chatService = new ChatService();
